import type {IssueSearchType} from '../types/issue-search-type'

const VALID_ISSUE_SEARCH_TYPES: ReadonlySet<IssueSearchType> = new Set([
  'ISSUE_ADVANCED',
  'ISSUE_HYBRID',
  'ISSUE_SEMANTIC',
])

/**
 * The fallback search type used when the value provided by the server payload is
 * missing or not a recognized `SearchType` enum value. Matches the GraphQL
 * default for `$type` on the issue index/search queries.
 */
export const DEFAULT_ISSUE_SEARCH_TYPE: IssueSearchType = 'ISSUE_ADVANCED'

/**
 * Type guard for the `issueSearchType` value that Rails sends through the relay
 * environment options. The value originates from a JSON payload and must match a
 * GraphQL `SearchType` enum value, so it is validated before being used to build
 * query variables or cache keys.
 */
export function isIssueSearchType(value: unknown): value is IssueSearchType {
  return typeof value === 'string' && VALID_ISSUE_SEARCH_TYPES.has(value as IssueSearchType)
}

/**
 * Reads and validates `options.issueSearchType` off the relay environment. This is
 * the single place the `options` cast and enum validation live, so the read hook
 * ({@link useIssueSearchType}) and the variable builders can't diverge. Returns the
 * validated value, or `undefined` when it is absent or not a recognized
 * `SearchType` — callers that build query variables skip setting `type` in that
 * case so the GraphQL default applies; the read hook falls back to
 * {@link DEFAULT_ISSUE_SEARCH_TYPE}.
 */
export function getIssueSearchTypeFromEnvironment(
  environment: {options?: unknown} | null | undefined,
): IssueSearchType | undefined {
  const issueSearchType = (environment?.options as {issueSearchType?: unknown} | undefined)?.issueSearchType
  return isIssueSearchType(issueSearchType) ? issueSearchType : undefined
}
